import {useMemo, useState, useCallback, useRef} from 'react'
import {MonoText} from '@/components/shared/MonoText'
import {useQueries, useMutation, useQueryClient} from '@tanstack/react-query'
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    pointerWithin,
    rectIntersection,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {CONCEPT_ICON} from '@/lib/concepts'
import type {CollisionDetection} from '@dnd-kit/core'
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {Globe, GripVertical, MoreHorizontal, Plus} from 'lucide-react'
import {ApiError} from '@/api/client'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {ContentArea} from '@/components/shared/ContentArea'
import {MutedText} from '@/components/shared/MutedText'
import {Stack} from '@/components/shared/Stack'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {PageLayout, TabPanelContent} from '@/components/shared/PageLayout'
import type {TabDefinition} from '@/components/shared/PageLayout'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {ErrorBanner} from '@/components/ui/error-banner'
import {Skeleton} from '@/components/ui/skeleton'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import {
    TypeScopeFilter,
    TYPE_FILTER_DROP_ID_ALL,
    TYPE_FILTER_DROP_ID_GLOBAL,
    TYPE_FILTER_DROP_ID_PREFIX,
} from '@/components/attributes/TypeScopeFilter'
import type {AttributeDefinition} from '@/types/attribute-definition'
import {AttributeDefinitionFilterParams, AttributeDefinitionResponseFields, AttributeDefinitionTabKeys} from '@/types/attribute-definition'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {useToast} from '@/hooks/use-toast'
import {useDialog} from '@/hooks/useDialog'
import {useConfirmedMutation} from '@/hooks/useConfirmedMutation'
import {DefinitionFormDialog} from '@/components/attributes/DefinitionFormDialog'
import {DefinitionImpactConfirmDialog} from '@/components/attributes/DefinitionImpactConfirmDialog'
import {MigrationJobBanner} from '@/components/attributes/MigrationJobBanner'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {DragHandle} from '@/components/shared/DragHandle'
import {DragPreview} from '@/components/shared/DragPreview'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'


const FILTER_ALL = 'all'
const FILTER_ACTIVE = AttributeDefinitionFilterParams.active
const FILTER_INACTIVE = 'inactive'

type ToggleableFlag =
    | typeof AttributeDefinitionResponseFields.searchable
    | typeof AttributeDefinitionResponseFields.filterable

// Custom collision detection: type filter droppables take priority over sortable rows.
// If the pointer is within a type filter droppable, return it. Otherwise fall back to
// closestCenter (for row-to-row reorder) and then rectIntersection as a last resort.
 
const typeFilterFirstCollision: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args)
    const typeFilterHit = pointerCollisions.find(c => {
        const id = String(c.id)
        return (
            id === TYPE_FILTER_DROP_ID_ALL ||
            id === TYPE_FILTER_DROP_ID_GLOBAL ||
            id.startsWith(TYPE_FILTER_DROP_ID_PREFIX)
        )
    })
    if (typeFilterHit) return [typeFilterHit]
    const centerCollisions = closestCenter(args)
    if (centerCollisions.length > 0) return centerCollisions
    return rectIntersection(args)
}
 


const ENTITY_TYPES = [
    {key: AttributeDefinitionTabKeys.identities, apiPath: AttributeEntityType.IDENTITY, hasTypeScope: true},
    {key: AttributeDefinitionTabKeys.parties, apiPath: AttributeEntityType.PARTY, hasTypeScope: true},
    {key: AttributeDefinitionTabKeys.resources, apiPath: AttributeEntityType.RESOURCE, hasTypeScope: true},
    {key: AttributeDefinitionTabKeys.functionGroups, apiPath: AttributeEntityType.FUNCTION_GROUP, hasTypeScope: true},
    {key: AttributeDefinitionTabKeys.targetSystems, apiPath: AttributeEntityType.TARGET_SYSTEM, hasTypeScope: true},
    {key: AttributeDefinitionTabKeys.connectors, apiPath: AttributeEntityType.CONNECTOR, hasTypeScope: true},
    {key: AttributeDefinitionTabKeys.reports, apiPath: AttributeEntityType.REPORT, hasTypeScope: false},
] as const

type EntityTypeKey = (typeof AttributeDefinitionTabKeys)[keyof typeof AttributeDefinitionTabKeys]

interface ActiveMigration {
    definitionId: string
    definitionName: string
    jobId: string
}

interface ReorderItem {
    id: string
    sortOrder: number
    version: number
}

interface UpdateTypeCodesPayload {
    definitionId: string
    newTypeCodes: string[]
}

// ============================================================================
// Sortable Row
// ============================================================================

interface SortableRowProps {
    def: AttributeDefinition
    canUpdate: boolean
    canDelete: boolean
    canReorder: boolean
    filterActive: boolean
    typeScopeFilter: string | null
    migration: ActiveMigration | undefined
    activeEntityType: {apiPath: AttributeEntityType}
    onEdit: (def: AttributeDefinition) => void
    onDelete: (def: AttributeDefinition) => void
    onRemoveFromType: (def: AttributeDefinition, typeCode: string) => void
    onMigrationCompleted: (id: string) => void
    onToggleFlag: (def: AttributeDefinition, flag: ToggleableFlag) => void
    i18n: ReturnType<typeof useI18n>
}

function SortableDefinitionRow({
    def,
    canUpdate,
    canDelete,
    canReorder,
    filterActive,
    typeScopeFilter,
    migration,
    activeEntityType,
    onEdit,
    onDelete,
    onRemoveFromType,
    onMigrationCompleted,
    onToggleFlag,
    i18n,
}: SortableRowProps) {
    const migrating = !!migration
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        id: def.id,
        disabled: !canReorder || filterActive,
    })

    const rowStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    const dragHandle = canReorder ? (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <DragHandle
                        {...(filterActive ? {} : attributes)}
                        {...(filterActive ? {} : listeners)}
                        data-testid={`definition-drag-handle-${def.name}`}
                        disabled={filterActive}
                        onClick={(e) => e.stopPropagation()}
                    />
                </TooltipTrigger>
                {filterActive && (
                    <TooltipContent>
                        {i18n.attributeDefinitions.reorder.disabledTooltip}
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    ) : null

    return (
        <TableRow
            ref={setNodeRef}
            style={rowStyle}
            data-testid={`definition-row-${def.name}`}
        >
            {canReorder && (
                <TableCell className="w-8 pr-0">
                    {dragHandle}
                </TableCell>
            )}
            <TableCell className="font-mono text-sm">
                <Stack gap="xs">
                    <InlineRow gap="sm" className="inline-flex">
                        {def.name}
                        {typeScopeFilter && typeScopeFilter !== '' && (!def.typeCodes || def.typeCodes.length === 0) && (
                            <Icon icon={Globe} size="xs" muted/>
                        )}
                    </InlineRow>
                    {migration && (
                        <MigrationJobBanner
                            definitionName={def.name}
                            jobId={migration.jobId}
                            entityType={activeEntityType.apiPath}
                            onCompleted={() => onMigrationCompleted(def.id)}
                        />
                    )}
                </Stack>
            </TableCell>
            <TableCell>{def.displayName}</TableCell>
            <TableCell><Badge variant="outline">{def.valueType}</Badge></TableCell>
            <TableCell>{def.required ? '\u2713' : '\u2014'}</TableCell>
            <TableCell>
                <Badge variant={def.active ? BadgeVariant.SUCCESS : BadgeVariant.WARNING}>
                    {def.active ? i18n.common.active : i18n.common.inactive}
                </Badge>
            </TableCell>
            <TableCell data-testid={`definition-searchable-${def.name}`}>
                <Badge variant={def.searchable ? BadgeVariant.SUCCESS : BadgeVariant.OUTLINE}>
                    {def.searchable ? i18n.common.yes : i18n.common.no}
                </Badge>
            </TableCell>
            <TableCell data-testid={`definition-filterable-${def.name}`}>
                <Badge variant={def.filterable ? BadgeVariant.SUCCESS : BadgeVariant.OUTLINE}>
                    {def.filterable ? i18n.common.yes : i18n.common.no}
                </Badge>
            </TableCell>
            <TableCell>{def.authorityName ?? '\u2014'}</TableCell>
            {(canUpdate || canDelete) && (
                <TableCell stickyRight>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`definition-row-menu-${def.name}`}
                                disabled={migrating}
                                title={migrating ? i18n.attributeDefinitions.migration.editDisabledDuringMigration : undefined}
                            >
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {canUpdate && (
                                <DropdownMenuItem
                                    disabled={migrating}
                                    onClick={() => onEdit(def)}
                                >
                                    {i18n.attributeDefinitions.editDefinition}
                                </DropdownMenuItem>
                            )}
                            {canUpdate && (
                                <DropdownMenuItem
                                    disabled={migrating}
                                    onClick={() => onToggleFlag(def, AttributeDefinitionResponseFields.searchable)}
                                    data-testid={`definition-action-toggle-searchable-${def.name}`}
                                >
                                    {def.searchable
                                        ? i18n.attributeDefinitions.flags.disableSearchable
                                        : i18n.attributeDefinitions.flags.enableSearchable}
                                </DropdownMenuItem>
                            )}
                            {canUpdate && (
                                <DropdownMenuItem
                                    disabled={migrating}
                                    onClick={() => onToggleFlag(def, AttributeDefinitionResponseFields.filterable)}
                                    data-testid={`definition-action-toggle-filterable-${def.name}`}
                                >
                                    {def.filterable
                                        ? i18n.attributeDefinitions.flags.disableFilterable
                                        : i18n.attributeDefinitions.flags.enableFilterable}
                                </DropdownMenuItem>
                            )}
                            {canDelete && typeScopeFilter && typeScopeFilter !== '' && def.typeCodes && def.typeCodes.length > 1 && def.typeCodes.includes(typeScopeFilter) ? (
                                <DropdownMenuItem
                                    className="text-destructive"
                                    disabled={migrating}
                                    onClick={() => onRemoveFromType(def, typeScopeFilter)}
                                    data-testid="definition-action-remove-from-type"
                                >
                                    {i18n.attributeDefinitions.removeFromType}
                                </DropdownMenuItem>
                            ) : canDelete ? (
                                <DropdownMenuItem
                                    className="text-destructive"
                                    disabled={migrating}
                                    onClick={() => onDelete(def)}
                                    data-testid="definition-action-delete"
                                >
                                    {i18n.attributeDefinitions.deleteDefinition}
                                </DropdownMenuItem>
                            ) : null}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </TableCell>
            )}
        </TableRow>
    )
}

// ============================================================================
// Main Component
// ============================================================================

export function AttributeDefinitions() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.attributeDefinitions.pageTitle)
    const {hasPermission} = useAuth()

    const canView = hasPermission(permissions.attributeDefinitions.view)
    const canCreate = hasPermission(permissions.attributeDefinitions.create)
    const canUpdate = hasPermission(permissions.attributeDefinitions.update)
    const canDelete = hasPermission(permissions.attributeDefinitions.delete)

    const {toast} = useToast()
    const queryClient = useQueryClient()

    const [activeTab, setActiveTab] = useState<EntityTypeKey>('identities')
    const [search, setSearch] = useState('')
    const [activeFilter, setActiveFilter] = useState<string>(FILTER_ALL)
    const [typeScopeFilter, setTypeScopeFilter] = useState<string | null>(null) // null=All, ''=Global, otherwise type code
    const createDialog = useDialog()
    const [editDefinition, setEditDefinition] = useState<AttributeDefinition | null>(null)
    const [activeMigrations, setActiveMigrations] = useState<ActiveMigration[]>([])

    // DnD state
    const [activeDragId, setActiveDragId] = useState<string | null>(null)
    const [announcement, setAnnouncement] = useState<string | null>(null)

    const activeEntityType = ENTITY_TYPES.find(t => t.key === activeTab)!

    // eslint-disable-next-line i18next/no-literal-string -- query key
    const queryKey = ['attribute-definitions', activeEntityType.apiPath] as const

    const definitionQueries = useQueries({
        queries: ENTITY_TYPES.map(et => ({
            // eslint-disable-next-line i18next/no-literal-string -- query key
            queryKey: ['attribute-definitions', et.apiPath] as const,
            queryFn: () => services.attributeDefinitions.list(et.apiPath),
            enabled: canView,
        })),
    })
    const activeQuery = definitionQueries[ENTITY_TYPES.findIndex(t => t.key === activeTab)]
    const definitions = useMemo(() => activeQuery?.data ?? [], [activeQuery?.data])
    const isLoading = activeQuery?.isLoading ?? false
    const error = activeQuery?.error ?? null

    // Optimistic reorder state — null means use server data
    const [optimisticOrder, setOptimisticOrder] = useState<AttributeDefinition[] | null>(null)
    const preDragOrderRef = useRef<AttributeDefinition[] | null>(null)

    // The displayed list: use optimistic order if set, else server data
    const displayedDefinitions = optimisticOrder ?? definitions

    // Reorder mutation
    const reorderMutation = useMutation({
        mutationFn: (items: ReorderItem[]) =>
            services.attributeDefinitions.reorder(activeEntityType.apiPath, {items}),
        onSuccess: () => {
            // Sync optimistic state into query cache and clear
            queryClient.setQueryData(queryKey, optimisticOrder ?? definitions)
            setOptimisticOrder(null)
            preDragOrderRef.current = null
        },
        onError: (err: ApiError) => {
            if (err.status === 409) {
                // Conflict: re-fetch from server, discard local optimistic state
                setOptimisticOrder(null)
                preDragOrderRef.current = null
                queryClient.invalidateQueries({queryKey})
            } else {
                // Other error: roll back to pre-drag order
                setOptimisticOrder(preDragOrderRef.current)
                preDragOrderRef.current = null
                toast({
                    variant: 'destructive',
                    description: i18n.attributeDefinitions.reorder.failedToast,
                })
                setTimeout(() => setOptimisticOrder(null), 0)
            }
        },
    })

    interface TypeAssignmentContext {
        previousTypeCodes: string[]
    }

    // Type assignment mutation — optimistic update with rollback
    const typeAssignmentMutation = useMutation<AttributeDefinition, ApiError, UpdateTypeCodesPayload, TypeAssignmentContext>({
        mutationFn: ({definitionId, newTypeCodes}: UpdateTypeCodesPayload) => {
            const def = definitions.find(d => d.id === definitionId)
            if (!def) throw new Error(`Definition not found: ${definitionId}`)
            return services.attributeDefinitions.update(
                activeEntityType.apiPath,
                definitionId,
                {
                    name: def.name,
                    displayName: def.displayName,
                    valueType: def.valueType,
                    authorityId: def.authorityId ?? undefined,
                    operations: def.operations,
                    validationRules: def.validationRules ?? undefined,
                    required: def.required,
                    sortOrder: def.sortOrder,
                    active: def.active,
                    version: def.version,
                    typeCodes: newTypeCodes,
                    confirmed: undefined,
                    migrationSql: undefined,
                },
            )
        },
        onMutate: ({definitionId, newTypeCodes}): TypeAssignmentContext => {
            // Capture current typeCodes before optimistic update
            const current = definitions.find(d => d.id === definitionId)
            const previousTypeCodes = current ? current.typeCodes : []
            // Apply optimistic update
            queryClient.setQueryData(queryKey, (old: AttributeDefinition[] | undefined) => {
                if (!old) return old
                return old.map(d => d.id === definitionId ? {...d, typeCodes: newTypeCodes} : d)
            })
            return {previousTypeCodes}
        },
        onSuccess: (_data, {definitionId, newTypeCodes}) => {
            // Sync the version bump from the server response into the cache
            queryClient.setQueryData(queryKey, (old: AttributeDefinition[] | undefined) => {
                if (!old) return old
                return old.map(d => d.id === definitionId ? {...d, typeCodes: newTypeCodes, version: _data.version} : d)
            })
            toast({description: i18n.attributeDefinitions.dragToType.successToast})
        },
        onError: (_err, {definitionId}, context) => {
            // Roll back the optimistic update
            if (context) {
                queryClient.setQueryData(queryKey, (old: AttributeDefinition[] | undefined) => {
                    if (!old) return old
                    return old.map(d => d.id === definitionId ? {...d, typeCodes: context.previousTypeCodes} : d)
                })
            }
            toast({
                variant: 'destructive',
                description: i18n.attributeDefinitions.dragToType.failedToast,
            })
        },
    })

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    )

    function handleDragStart(event: DragStartEvent) {
        setActiveDragId(event.active.id as string)
        // Capture pre-drag order from current displayed list (filtered if needed)
        preDragOrderRef.current = displayedDefinitions
    }

     
    function handleDragEnd(event: DragEndEvent) {
        setActiveDragId(null)
        const {active, over} = event
        if (!over) {
            preDragOrderRef.current = null
            return
        }

        const overId = over.id as string

        // Check if dropped onto a type filter button
        if (overId === TYPE_FILTER_DROP_ID_ALL) {
            // Drop on "All" — no-op
            preDragOrderRef.current = null
            return
        }

        if (overId === TYPE_FILTER_DROP_ID_GLOBAL || overId.startsWith(TYPE_FILTER_DROP_ID_PREFIX)) {
            // Dropped onto a type filter button
            preDragOrderRef.current = null
            const draggedDef = displayedDefinitions.find(d => d.id === active.id)
            if (!draggedDef) return

            let newTypeCodes: string[]
            if (overId === TYPE_FILTER_DROP_ID_GLOBAL) {
                // Drop on "Global" — clear all typeCodes
                if (draggedDef.typeCodes.length === 0) {
                    // Already global — silent no-op
                    return
                }
                newTypeCodes = []
            } else {
                // Drop on a specific type button — add type code
                const typeCode = overId.slice(TYPE_FILTER_DROP_ID_PREFIX.length)
                if (draggedDef.typeCodes.includes(typeCode)) {
                    // Already has this type code — silent no-op
                    return
                }
                newTypeCodes = [...draggedDef.typeCodes, typeCode]
            }

            typeAssignmentMutation.mutate({definitionId: draggedDef.id, newTypeCodes})
            return
        }

        // Normal sortable reorder
        if (active.id === over.id) {
            preDragOrderRef.current = null
            return
        }

        const currentList = displayedDefinitions
        const oldIndex = currentList.findIndex(d => d.id === active.id)
        const newIndex = currentList.findIndex(d => d.id === over.id)
        if (oldIndex === -1 || newIndex === -1) {
            preDragOrderRef.current = null
            return
        }

        const reordered = arrayMove(currentList, oldIndex, newIndex)

        // Screen-reader announcement
        const ann = i18n.attributeDefinitions.reorder.announcement({from: oldIndex + 1, to: newIndex + 1})
        setAnnouncement(ann)
        setTimeout(() => setAnnouncement(null), 3000)

        // Optimistic update
        setOptimisticOrder(reordered)

        // Build reorder items: assign sortOrder = index
        const items: ReorderItem[] = reordered.map((d, idx) => ({
            id: d.id,
            sortOrder: idx,
            version: d.version,
        }))

        reorderMutation.mutate(items)
    }
     

    const deleteMutation = useConfirmedMutation<void, AttributeDefinition>({
        mutationFn: (def) =>
            services.attributeDefinitions.remove(activeEntityType.apiPath, def.id, {confirmed: def.confirmed, migrationSql: def.migrationSql}),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey})
            toast({description: i18n.attributeDefinitions.deleteSuccess})
        },
    })

    function handleDelete(def: AttributeDefinition) {
        deleteMutation.mutation.mutate(def)
    }

    const removeFromTypeMutation = useConfirmedMutation<AttributeDefinition, {def: AttributeDefinition; typeCode: string}>({
        mutationFn: ({def, typeCode}) => {
            const newTypeCodes = (def.typeCodes || []).filter(c => c !== typeCode)
            return services.attributeDefinitions.update(activeEntityType.apiPath, def.id, {
                name: def.name,
                displayName: def.displayName,
                valueType: def.valueType,
                authorityId: def.authorityId ?? undefined,
                operations: def.operations,
                validationRules: def.validationRules ?? undefined,
                required: def.required,
                sortOrder: def.sortOrder,
                active: def.active,
                version: def.version,
                typeCodes: newTypeCodes,
            })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey})
            toast({description: i18n.attributeDefinitions.removeFromTypeSuccess})
        },
    })

    function handleRemoveFromType(def: AttributeDefinition, typeCode: string) {
        removeFromTypeMutation.mutation.mutate({def, typeCode})
    }

    const toggleFlagMutation = useMutation<AttributeDefinition, ApiError, {def: AttributeDefinition; flag: ToggleableFlag}>({
        mutationFn: ({def, flag}) => services.attributeDefinitions.update(activeEntityType.apiPath, def.id, {
            name: def.name,
            displayName: def.displayName,
            valueType: def.valueType,
            authorityId: def.authorityId ?? undefined,
            operations: def.operations,
            validationRules: def.validationRules ?? undefined,
            required: def.required,
            sortOrder: def.sortOrder,
            active: def.active,
            searchable: flag === AttributeDefinitionResponseFields.searchable ? !def.searchable : def.searchable,
            filterable: flag === AttributeDefinitionResponseFields.filterable ? !def.filterable : def.filterable,
            version: def.version,
            typeCodes: def.typeCodes,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey})
            toast({description: i18n.attributeDefinitions.flags.updateSuccess})
        },
        onError: () => {
            toast({
                variant: 'destructive',
                description: i18n.attributeDefinitions.flags.updateFailed,
            })
        },
    })

    function handleToggleFlag(def: AttributeDefinition, flag: ToggleableFlag) {
        toggleFlagMutation.mutate({def, flag})
    }

    const handleMigrationStarted = useCallback((definitionId: string, jobId: string) => {
        const def = definitions.find(d => d.id === definitionId)
        if (def) {
            setActiveMigrations(prev => [...prev, {definitionId, definitionName: def.name, jobId}])
        }
    }, [definitions])

    const handleMigrationCompleted = useCallback((definitionId: string) => {
        setActiveMigrations(prev => prev.filter(m => m.definitionId !== definitionId))
    }, [])

    // Filter is applied to displayed definitions
    const filtered = displayedDefinitions.filter(def => {
        if (activeFilter === FILTER_ACTIVE && !def.active) return false
        if (activeFilter === FILTER_INACTIVE && def.active) return false
        if (search) {
            const q = search.toLowerCase()
            if (!def.name.toLowerCase().includes(q) && !def.displayName.toLowerCase().includes(q)) return false
        }
        // Type scope filter only applies to entity types that have type scopes
        if (activeEntityType.hasTypeScope) {
            if (typeScopeFilter === '') {
                // Global: show only definitions with empty typeCodes
                if (def.typeCodes && def.typeCodes.length > 0) return false
            } else if (typeScopeFilter !== null) {
                // Specific type: show definitions scoped to this type OR global (empty typeCodes)
                const isGlobal = !def.typeCodes || def.typeCodes.length === 0
                const includesType = def.typeCodes && def.typeCodes.includes(typeScopeFilter)
                if (!isGlobal && !includesType) return false
            }
        }
        return true
    })

    // Drag is disabled when type scope filter is active (not "All")
    const filterActive = activeEntityType.hasTypeScope && typeScopeFilter !== null
    // Drag handles only shown for admins who can update
    const canReorder = canUpdate

    // Type filter buttons are droppable only when: entity supports type scope, filter is NOT active, user can update
    const typeScopeDroppable = activeEntityType.hasTypeScope && !filterActive && canUpdate

    // The drag overlay shows the definition name for the dragged row
    const activeDragDef = activeDragId ? displayedDefinitions.find(d => d.id === activeDragId) : null

    if (!canView) {
        return (
            <MutedText>{i18n.attributeDefinitions.noPermission}</MutedText>
        )
    }

    const handleTabChange = (tab: string) => {
        setActiveTab(tab as EntityTypeKey)
        setSearch('')
        setTypeScopeFilter(null)
        setOptimisticOrder(null)
    }

    const entityTypeTabs: TabDefinition[] = ENTITY_TYPES.map((et, i) => {
        const total = definitionQueries[i]?.data?.length ?? 0
        return {
            key: et.key,
            label: String(i18n.attributeDefinitions.tabs[et.key]),
            count: total > 0 ? total : undefined,
        }
    })

    return (
        <PageLayout
            variant="tabbed-list"
            icon={CONCEPT_ICON.attributeDefinition}
            title={i18n.attributeDefinitions.title}
            description={i18n.attributeDefinitions.subtitle}
            tabs={entityTypeTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            data-testid="attribute-definitions-page"
            actions={canCreate ? (
                <Button
                    size="sm"
                    data-testid="create-definition-button"
                    onClick={createDialog.open}
                >
                    <Icon icon={Plus} className="mr-2"/>
                    {i18n.attributeDefinitions.createDefinition}
                </Button>
            ) : undefined}
        >
            <TabPanelContent>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={typeFilterFirstCollision}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                >
                                    {/* Type scope filter only for entity types that support it */}
                                    {activeEntityType.hasTypeScope && (
                                        <Stack className="mb-4">
                                            <TypeScopeFilter
                                                entityType={activeEntityType.apiPath}
                                                value={typeScopeFilter}
                                                onChange={setTypeScopeFilter}
                                                droppable={typeScopeDroppable}
                                                getCount={(scope) => {
                                                    if (scope === null) return definitions.length
                                                    if (scope === '') {
                                                        return definitions.filter(d => !d.typeCodes || d.typeCodes.length === 0).length
                                                    }
                                                    return definitions.filter(d => {
                                                        const isGlobal = !d.typeCodes || d.typeCodes.length === 0
                                                        return isGlobal || (d.typeCodes?.includes(scope) ?? false)
                                                    }).length
                                                }}
                                            />
                                        </Stack>
                                    )}

                                    {/* Reorder disabled banner */}
                                    {canReorder && filterActive && (
                                        <InfoBanner variant="muted" className="mb-4" data-testid="reorder-disabled-banner">
                                            {i18n.attributeDefinitions.reorder.banner}
                                        </InfoBanner>
                                    )}

                                    <InlineRow gap="lg" className="mb-4">
                                        <Input
                                            placeholder={i18n.common.search}
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                            className="max-w-xs"
                                        />
                                        <Select value={activeFilter} onValueChange={setActiveFilter}>
                                            <SelectTrigger className="w-[140px]" data-testid="definition-active-filter">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={FILTER_ALL}>{i18n.common.all}</SelectItem>
                                                <SelectItem value={FILTER_ACTIVE}>{i18n.common.active}</SelectItem>
                                                <SelectItem value={FILTER_INACTIVE}>{i18n.common.inactive}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </InlineRow>

                                    {isLoading ? (
                                        <Stack gap="sm">
                                            {Array.from({length: 5}).map((_, i) => (
                                                <Skeleton key={i} className="h-10 w-full" />
                                            ))}
                                        </Stack>
                                    ) : error ? (
                                        <ErrorBanner error={error} />
                                    ) : filtered.length === 0 ? (
                                        <InlineEmptyText>{i18n.attributeDefinitions.empty}</InlineEmptyText>
                                    ) : (
                                        <ContentArea className="overflow-x-auto">
                                            <SortableContext
                                                items={filtered.map(d => d.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            {canReorder && <TableHead className="w-8" data-testid="col-drag-handle"/>}
                                                            <TableHead data-testid="col-name">{i18n.attributeDefinitions.columns.name}</TableHead>
                                                            <TableHead data-testid="col-display-name">{i18n.attributeDefinitions.columns.displayName}</TableHead>
                                                            <TableHead data-testid="col-value-type">{i18n.attributeDefinitions.columns.valueType}</TableHead>
                                                            <TableHead data-testid="col-required">{i18n.attributeDefinitions.columns.required}</TableHead>
                                                            <TableHead data-testid="col-active">{i18n.attributeDefinitions.columns.active}</TableHead>
                                                            <TableHead data-testid="col-searchable">{i18n.attributeDefinitions.columns.searchable}</TableHead>
                                                            <TableHead data-testid="col-filterable">{i18n.attributeDefinitions.columns.filterable}</TableHead>
                                                            <TableHead data-testid="col-authority">{i18n.attributeDefinitions.columns.authority}</TableHead>
                                                            {(canUpdate || canDelete) && <TableHead stickyRight data-testid="col-actions"/>}
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {filtered.map(def => (
                                                            <SortableDefinitionRow
                                                                key={def.id}
                                                                def={def}
                                                                canUpdate={canUpdate}
                                                                canDelete={canDelete}
                                                                canReorder={canReorder}
                                                                filterActive={filterActive}
                                                                typeScopeFilter={typeScopeFilter}
                                                                migration={activeMigrations.find(m => m.definitionId === def.id)}
                                                                activeEntityType={activeEntityType}
                                                                onEdit={setEditDefinition}
                                                                onDelete={handleDelete}
                                                                onRemoveFromType={handleRemoveFromType}
                                                                onMigrationCompleted={handleMigrationCompleted}
                                                                onToggleFlag={handleToggleFlag}
                                                                i18n={i18n}
                                                            />
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </SortableContext>

                                            <DragOverlay>
                                                {activeDragDef ? (
                                                    <DragPreview>
                                                        <Icon icon={GripVertical} muted/>
                                                        <MonoText>{activeDragDef.name}</MonoText>
                                                        <MutedText>{activeDragDef.displayName}</MutedText>
                                                    </DragPreview>
                                                ) : null}
                                            </DragOverlay>
                                        </ContentArea>
                                    )}
                                </DndContext>
            </TabPanelContent>

            {/* Screen-reader live region for DnD announcements */}
            <div aria-live="polite" aria-atomic="true">
                <MutedText className="sr-only">{announcement}</MutedText>
            </div>

            <DefinitionFormDialog
                open={createDialog.isOpen || !!editDefinition}
                onOpenChange={open => { if (!open) createDialog.close() }}
                onClosed={() => setEditDefinition(null)}
                entityType={activeEntityType.apiPath}
                definition={editDefinition}
                onMigrationStarted={handleMigrationStarted}
            />

            <DefinitionImpactConfirmDialog
                open={deleteMutation.impactDialogOpen || removeFromTypeMutation.impactDialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        deleteMutation.cancelImpactDialog()
                        removeFromTypeMutation.cancelImpactDialog()
                    }
                }}
                impact={deleteMutation.impact ?? removeFromTypeMutation.impact}
                onConfirm={() => {
                    if (deleteMutation.impactDialogOpen) {
                        deleteMutation.confirmAndResend()
                    } else {
                        removeFromTypeMutation.confirmAndResend()
                    }
                }}
                isSaving={deleteMutation.mutation.isPending || removeFromTypeMutation.mutation.isPending}
            />
        </PageLayout>
    )
}
